import { Box, Flex, Input, InputGroup, Show, useToken } from '@chakra-ui/react';
import { useState } from 'react';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftText from '@/components/Atoms/FaceliftText';

interface PasswordConfirmationDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (password: string) => void;
	title: string;
	description: string;
	isLoading?: boolean;
}

const PasswordConfirmationDialog = ({
	open,
	onOpenChange,
	onConfirm,
	title,
	description,
	isLoading = false,
}: PasswordConfirmationDialogProps) => {
	const [brand600] = useToken('colors', ['brand.600']);
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState('');

	const handleConfirm = () => {
		if (!password.trim()) {
			setError('Password is required');
			return;
		}
		setError('');
		onConfirm(password);
		setPassword('');
		setShowPassword(false);
	};

	const handleCancel = () => {
		setPassword('');
		setShowPassword(false);
		setError('');
		onOpenChange(false);
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPassword(e.target.value);
		if (error) {
			setError('');
		}
	};

	return (
		<FaceliftDialog
			open={open}
			onOpenChange={(open) => {
				if (!open) {
					handleCancel();
				}
			}}
			title={title}
			showCloseBtn={false}
			showBackBtn={false}
			sx={{
				boxShadow: `0 0 20px 0px ${brand600}`,
				width: '500px',
				maxWidth: '95vw',
				borderRadius: '15px',
			}}
		>
			<Flex direction="column" gap={6} py={2}>
				<FaceliftText fontSize="sm" color="brand.textDark">
					{description}
				</FaceliftText>

				<Box>
					<FaceliftText
						fontSize="sm"
						fontWeight="medium"
						color="brand.textDark"
						mb={2}
					>
						Enter your password to continue
					</FaceliftText>
					<InputGroup
						endElement={
							<FaceliftIconButton
								bg="transparent"
								variant="ghost"
								aria-label="toggle password visibility"
								size="xs"
								onClick={() => setShowPassword(!showPassword)}
								_hover={{
									bg: { _dark: 'gray.800', _light: 'gray.300' },
								}}
								color={{ _dark: 'brand', _light: 'brand.500' }}
							>
								{showPassword ? <LuEye size={16} /> : <LuEyeOff size={16} />}
							</FaceliftIconButton>
						}
					>
						<Input
							value={password}
							onChange={handlePasswordChange}
							variant="outline"
							bg="brand.inputBg"
							size="md"
							placeholder="Enter your password"
							_placeholder={{
								color: { _dark: 'brand.lightGray' },
							}}
							type={showPassword ? 'text' : 'password'}
							borderColor={error ? 'brand.errors' : undefined}
							disabled={isLoading}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									handleConfirm();
								}
							}}
						/>
					</InputGroup>
					<Show when={!!error}>
						<FaceliftText color="brand.errors" fontSize="xs" mt={1} ml={1}>
							{error}
						</FaceliftText>
					</Show>
				</Box>

				<Flex
					gap={3}
					justifyContent="flex-end"
					flexDirection={{ base: 'column', sm: 'row' }}
				>
					<FaceliftButton
						variant="outline"
						bg="white"
						color="gray.800"
						border="1px solid"
						width={{ base: 'full', sm: '32' }}
						onClick={handleCancel}
						disabled={isLoading}
					>
						Cancel
					</FaceliftButton>
					<FaceliftButton
						width={{ base: 'full', sm: '32' }}
						onClick={handleConfirm}
						loading={isLoading}
						disabled={isLoading}
					>
						Confirm
					</FaceliftButton>
				</Flex>
			</Flex>
		</FaceliftDialog>
	);
};

export default PasswordConfirmationDialog;
